import React, { useState, useEffect } from 'react';
import './Activities.css';
import Footer from '../../components/Footer/Footer';
import { fetchActivities } from '../../services/api';

const Activities = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [activitiesData, setActivitiesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
      localStorage.setItem('selectedLanguage', event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Fetch activities from API
  useEffect(() => {
    const loadActivities = async () => {
      try {
        setLoading(true);
        const activities = await fetchActivities();
        setActivitiesData(activities);
      } catch (error) {
        console.error('Failed to load activities:', error);
        setActivitiesData([]);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);


  const content = {
    EN: {
      title: 'Reboon Organization Activities',
      subtitle: 'Discover the various activities and initiatives we undertake to serve our community and spread Islamic knowledge.'
    },
    KU: {
      title: 'کاروچالاکیەکانی ڕێکخراوی ڕێبوون',
      subtitle: 'کاروچالاکی و بیرۆکە جۆراو جۆرەکانمان بزانە کە دەیکەین بۆ خزمەتکردنی کۆمەڵگاکەمان و بڵاوکردنەوەی زانیاری ئیسلامی.'
    },
    AR: {
      title: 'أنشطة منظمة ریبوون',
      subtitle: 'اكتشف الأنشطة والمبادرات المختلفة التي نقوم بها لخدمة مجتمعنا ونشر المعرفة الإسلامية.'
    },
    TR: {
      title: 'Reboon Organizasyonu Faaliyetleri',
      subtitle: 'Topluluğumuza hizmet etmek ve İslami bilgiyi yaymak için üstlendiğimiz çeşitli faaliyetleri ve girişimleri keşfedin.'
    }
  };

  const pageContent = content[currentLang] || content.KU;

  return (
    <div className="activities-page">
      <div className="container">
        <div className="activities-content">
          {/* Title Section */}
          <div className="activities-title-section">
            <h1 className="page-title">{pageContent.title}</h1>
            <p className="page-subtitle">{pageContent.subtitle}</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading activities...</p>
            </div>
          )}

          {/* Activities Grid */}
          {!loading && (
            <div className="activities-grid">
              {activitiesData.map((activity) => {
                // Transform API data to match component structure
                const activityContent = {
                  title: activity.title?.[currentLang] || activity.title?.EN || '',
                  description: activity.description?.[currentLang] || activity.description?.EN || ''
                };

                // Transform image URLs from API
                const images = (activity.images || []).map(img =>
                  typeof img === 'string' ? img : (img.url || img.src || img)
                );

                return (
                  <div key={activity.id} className="activity-card">
                    {/* Activity Images */}
                    {images && images.length > 0 && (
                      <div className="activity-images-container">
                        {images.length === 1 ? (
                          <div
                            className="activity-image-single"
                            onClick={() => setSelectedImage(images[0])}
                          >
                            <img
                              src={images[0]}
                              alt={activityContent.title}
                              className="activity-image"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="activity-images-grid">
                            {images.map((img, index) => (
                              <div
                                key={index}
                                className="activity-image-item"
                                onClick={() => setSelectedImage(img)}
                              >
                                <img
                                  src={img}
                                  alt={`${activityContent.title} ${index + 1}`}
                                  className="activity-image"
                                  loading="lazy"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Activity Content */}
                    <div className="activity-content">
                      <h2 className="activity-title">{activityContent.title}</h2>
                      {activity.date && (
                        <p className="activity-date">
                          <svg className="date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          {activity.date}
                        </p>
                      )}
                      <p className="activity-description">{activityContent.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {!loading && activitiesData.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No activities found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={selectedImage} alt="Enlarged view" />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Activities;
